import {
	ConstructionSiteFullDetails,
	GFAFullDetails,
	PAGES,
	TemplateType,
	PropositionDetails,
	ContractStatus,
	ContractStatusValues,
} from "@dune/types";
import { Card, DataTable, ExternalLinkIcon } from "@dune/ui";
import {
	formatPricing,
	getClientNames,
	getMOAName,
	getRealEstateName,
	getSyndicName,
} from "@dune/utils";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { isGFAConstructionSite } from "../../../../utils/gfa/gfa.utils";
import { constructionSiteOffersColumns } from "./GeneralTab.columns";
import { indicationTarifaireTemplateTypes } from "@dune/vars";
import { useGetConstructionSiteDocuments } from "../../../../api/queries/constructionSites.queries";

type SimplePropositionType = {
	id: number;
	status: string;
	reference: string;
	productType: string;
	primeTTC: number;
	remunerationApporteur: number;
};

interface GeneralTabProps {
	constructionSite: ConstructionSiteFullDetails | GFAFullDetails;
}

interface GeneralInfoCardsProps {
	constructionSite: ConstructionSiteFullDetails | GFAFullDetails;
}

interface PropositionsSectionProps {
	title: string;
	propositions: (PropositionDetails | SimplePropositionType)[];
	emptyMessage: string;
	getLinkPath: (propositionId: number) => string;
}

interface PropositionItemCardProps {
	proposition: PropositionDetails | SimplePropositionType;
	href: string;
}

const isContractProposition = (
	proposition: PropositionDetails | SimplePropositionType,
): boolean => {
	const contractStatusValues = Object.values(ContractStatus);
	return contractStatusValues.includes(
		proposition.status as ContractStatusValues,
	);
};

const separatePropositionsAndContracts = (
	propositions: (PropositionDetails | SimplePropositionType)[] | undefined,
): {
	proposals: (PropositionDetails | SimplePropositionType)[];
	contracts: (PropositionDetails | SimplePropositionType)[];
} => {
	if (!propositions) {
		return { proposals: [], contracts: [] };
	}

	const proposals = propositions.filter((prop) => !isContractProposition(prop));
	const contracts = propositions.filter((prop) => isContractProposition(prop));

	return { proposals, contracts };
};

const PropositionItemCard: React.FC<PropositionItemCardProps> = ({
	proposition,
	href,
}) => {
	return (
		<Link to={href} className="block">
			<Card className="p-4 bg-white border shadow-none hover:shadow-md hover:border-gray-300 transition-all duration-200 cursor-pointer">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-4">
						<div className="flex flex-col">
							<div className="flex items-center gap-2 mb-2">
								<span className="font-semibold text-gray-900 uppercase text-sm">
									{proposition.reference ?? `PROP-${proposition.id}`} (
									{proposition.productType})
								</span>
							</div>
						</div>
					</div>
					<ExternalLinkIcon className="size-6 text-dune-primary flex-shrink-0" />
				</div>
			</Card>
		</Link>
	);
};

const PropositionsSection: React.FC<PropositionsSectionProps> = ({
	title,
	propositions,
	emptyMessage,
	getLinkPath,
}) => {
	return (
		<div>
			<h3 className="text-lg font-semibold mb-4">{title}</h3>
			<Card className="p-6">
				{propositions && propositions.length > 0 ? (
					<div className="space-y-4">
						{propositions.map((proposition) => (
							<PropositionItemCard
								key={proposition.id}
								proposition={proposition}
								href={getLinkPath(proposition.id)}
							/>
						))}
					</div>
				) : (
					<div className="text-center text-gray-500">{emptyMessage}</div>
				)}
			</Card>
		</div>
	);
};

const GeneralInfoCards: React.FC<GeneralInfoCardsProps> = ({
	constructionSite,
}) => {
	const isGFA = isGFAConstructionSite(constructionSite);
	const clientNames = getClientNames(
		constructionSite.constructionSiteClientDetails,
	);
	const moaNames = getMOAName(constructionSite.constructionSiteClientDetails);
	const syndicName = getSyndicName(constructionSite);
	const primeTTC =
		"propositions" in constructionSite
			? constructionSite.propositions.reduce(
					(sum, proposition) => sum + (proposition.primeTTC || 0),
					0,
				)
			: null;
	const remunerationApporteur =
		"propositions" in constructionSite
			? constructionSite.propositions.reduce(
					(sum, proposition) => sum + (proposition.remunerationApporteur || 0),
					0,
				)
			: null;

	return (
		<div
			className={`grid grid-cols-1 gap-4 ${syndicName ? "md:grid-cols-3" : "md:grid-cols-4"}`}
		>
			{syndicName && (
				<Card className="p-4">
					<div className="flex items-center gap-2 mb-2">
						<span className="text-sm font-medium text-gray-700">Syndic</span>
					</div>
					<div className="flex flex-col text-lg font-semibold">
						<div className="flex flex-col gap-2">
							<span>{syndicName}</span>
						</div>
					</div>
				</Card>
			)}
			{!syndicName && primeTTC !== null && (
				<Card className="p-4 gap-0">
					<div className="flex items-center mb-2">
						<span className="text-sm font-medium text-gray-700">Prime TTC</span>
					</div>
					<div className="flex flex-col text-lg font-semibold">
						{formatPricing(primeTTC)}
					</div>
				</Card>
			)}
			{!syndicName && remunerationApporteur !== null && (
				<Card className="p-4 gap-0">
					<div className="flex items-center gap-2 mb-2">
						<span className="text-sm font-medium text-gray-700">
							Rémunération Apporteur
						</span>
					</div>
					<div className="flex flex-col text-lg font-semibold">
						{formatPricing(remunerationApporteur)}
					</div>
				</Card>
			)}

			<Card className="p-4">
				<div className="flex items-center gap-2 mb-2">
					<span className="text-sm font-medium text-gray-700">
						{isGFA ? "Promoteur" : "Client"}
					</span>
				</div>

				<div className="flex flex-col text-lg font-semibold">
					{isGFA ? (
						getRealEstateName(constructionSite)
					) : (
						<div className="flex flex-col gap-2">
							{clientNames.map((client: string, index: number) => {
								return (
									<div key={client} className="flex flex-col gap-2">
										<span>{client}</span>
										{index < clientNames.length - 1 && (
											<span className="text-xs text-gray-500">-</span>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>
			</Card>

			<Card className="p-4">
				<div className="flex items-center gap-2 mb-2">
					<span className="text-sm font-medium text-gray-700">MOA</span>
				</div>
				<div className="flex flex-col text-lg font-semibold">
					{Array.isArray(moaNames) ? (
						<div className="flex flex-col gap-2">
							{moaNames.map((client: string, index: number) => {
								return (
									<div key={client} className="flex flex-col gap-2">
										<span key={client}>{client}</span>
										{index < moaNames.length - 1 && (
											<span className="text-xs text-gray-500">-</span>
										)}
									</div>
								);
							})}
						</div>
					) : (
						moaNames
					)}
				</div>
			</Card>
		</div>
	);
};

const GeneralTab: React.FC<GeneralTabProps> = ({ constructionSite }) => {
	const isGFA = isGFAConstructionSite(constructionSite);

	const { data: documentsData } = useGetConstructionSiteDocuments(
		constructionSite.id,
		{ page: 1, limit: 100 },
		{ enabled: !isGFA && !!constructionSite.id },
	);

	const constructionSiteOffers = useMemo(() => {
		if (isGFA) return [];

		const offerFiles = (documentsData?.data || [])
			.filter((file) => file.warrantyType && file.insurerType)
			.sort(
				(a, b) =>
					new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
			);

		const uniqueFilesByTemplate = new Map<string, (typeof offerFiles)[0]>();

		for (const file of offerFiles) {
			const { warrantyType, insurerType } = file;
			if (!warrantyType || !insurerType) continue;

			const templateKey: TemplateType | null =
				indicationTarifaireTemplateTypes[`${warrantyType}_${insurerType}`] ??
				null;
			if (!templateKey) continue;

			if (!uniqueFilesByTemplate.has(templateKey)) {
				uniqueFilesByTemplate.set(templateKey, file);
			}
		}

		return Array.from(uniqueFilesByTemplate.values()).map((file) => ({
			id: file.id,
			createdAt: file.createdAt,
			warrantyType: file.warrantyType,
			insurerType: file.insurerType,
			files: [file],
			isDeleted: file.isDeleted,
		}));
	}, [isGFA, documentsData]);

	const { proposals, contracts } = separatePropositionsAndContracts(
		constructionSite.propositions,
	);

	return (
		<div className="flex flex-col gap-6 p-6">
			<GeneralInfoCards constructionSite={constructionSite} />

			{!isGFA && (
				<div>
					<p className="text-lg font-semibold mb-4">Indications tarifaires</p>
					<DataTable
						columns={constructionSiteOffersColumns}
						data={constructionSiteOffers}
						noResultsText="Aucune indication tarifaire disponible"
						variant="static-clean"
					/>
				</div>
			)}

			<PropositionsSection
				title="Propositions"
				propositions={proposals}
				emptyMessage={
					isGFA
						? "Aucune proposition disponible pour les chantiers GFA"
						: "Aucune proposition à afficher"
				}
				getLinkPath={(id) => PAGES.PROPOSALS.show.withParams(id.toString())}
			/>

			{contracts.length > 0 && (
				<PropositionsSection
					title="Contrats"
					propositions={contracts}
					emptyMessage="Aucun contrat disponible"
					getLinkPath={(id) => PAGES.CONTRACTS.show.withParams(id.toString())}
				/>
			)}
		</div>
	);
};

export default GeneralTab;
